// Helpers for logging time by typing an amount instead of running the timer.
// Kept free of React so the parsing and the placement rules can be tested on
// their own.

// The duration field is a digits-only mask that fills left to right:
// H H : M M. Anything that isn't a digit is dropped, at most four are kept,
// and positions not typed yet count as 0, so "2" is 20:00, "23" is 23:00,
// "0130" is 01:30 and "0023" is 23 minutes.

// durationDigits keeps only the digits of what was typed or pasted, at most
// four of them ("1h30" gives "130").
export const durationDigits = (input) => String(input || "").replace(/\D/g, "").slice(0, 4);

// maskDuration shows the digits as they're typed, with the colon once a third
// digit exists: "2", "23", "23:0", "23:00".
export const maskDuration = (input) => {
  const digits = durationDigits(input);
  return digits.length > 2 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : digits;
};

// ghostDuration is the part of the "00:00" template not typed yet, shown dim
// after the typed text so the whole HH:MM shape is always visible: "" gives
// "00:00", "2" gives "0:00", "23" gives ":00", "2300" gives "".
export const ghostDuration = (input) => "00:00".slice(maskDuration(input).length);

// parseDurationDigits reads the digits as HH:MM, filling missing positions
// with 0, and returns the total in seconds, or null when nothing is typed,
// the hours go past 23, the minutes past 59, or the total is zero. The
// longest amount is 23:59, so an entry that crosses midnight splits into two
// parts at most.
export const parseDurationDigits = (input) => {
  const digits = durationDigits(input);
  if (!digits) return null;
  const padded = digits.padEnd(4, "0");
  const h = Number(padded.slice(0, 2));
  const m = Number(padded.slice(2));
  if (h > 23 || m > 59) return null;
  const seconds = h * 3600 + m * 60;
  return seconds > 0 ? seconds : null;
};

const pad = (n) => String(n).padStart(2, "0");

// formatDuration is how the field shows a valid amount once the user leaves
// it: HH:MM with leading zeros (5400 gives "01:30").
export const formatDuration = (seconds) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${pad(h)}:${pad(m)}`;
};

// localDay gives the user's own calendar date as YYYY-MM-DD. toISOString()
// would give the UTC date, which is yesterday or tomorrow near midnight.
export const localDay = (date = new Date()) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

// localTime gives the time of day as HH:MM, the way a native time input
// holds it; the seconds are added only when there are some.
export const localTime = (date = new Date()) => {
  const hm = `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  return date.getSeconds() ? `${hm}:${pad(date.getSeconds())}` : hm;
};

// startFrom turns the start fields (YYYY-MM-DD and HH:MM or HH:MM:SS) into a
// local date and time, or null while either is empty or half typed.
export const startFrom = (day, time) => {
  const d = String(day || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const t = String(time || "").match(/^(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!d || !t) return null;
  return new Date(Number(d[1]), Number(d[2]) - 1, Number(d[3]), Number(t[1]), Number(t[2]), Number(t[3] || 0));
};

const clockOf = (date) => `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;

// placeEntry turns a start and a length into the entries to save. cwclock
// keeps an entry within one day, so one that runs past midnight is split
// there the way the server splits a running timer: the first part ends at
// 23:59:59, the second starts at 00:00:00 the next day. An entry ending
// exactly at midnight stays whole and ends at 23:59:59. Durations stay under
// a day, so there are never more than two parts. Milliseconds are dropped
// from the start so the parts line up on whole seconds.
export const placeEntry = (startDate, seconds) => {
  const start = new Date(startDate.getTime());
  start.setMilliseconds(0);
  const end = new Date(start.getTime() + seconds * 1000);
  const midnight = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 1);
  if (end <= midnight) {
    return [{ day: localDay(start), start: clockOf(start), end: end < midnight ? clockOf(end) : "23:59:59" }];
  }
  return [
    { day: localDay(start), start: clockOf(start), end: "23:59:59" },
    { day: localDay(midnight), start: "00:00:00", end: clockOf(end) },
  ];
};
